import { bumpLabel, type ApprovalStateDto, type AttachmentDto, type DiagramView, type DiffResponseDto, type FlagDto, type GraphResponseDto, type MeResponse, type ProcessDetailDto, type ProcessGraph, type ProcessInterviewDto, type RaciLetter, type Step, type VersionBump } from '@process-hub/shared';
import { Lightbox, ScreenshotManager, StepScreenshots } from '../components/Screenshots';
import { describeError } from '../errors';
import { FlagsPanel } from '../components/FlagsPanel';
import { WalkthroughVideos } from '../components/Videos';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { sharePointNote } from '../sharepoint';
import { ApprovalPanel, STAGE_LABEL } from '../components/ApprovalPanel';
import { minutes, Performer, RACI_DEFINITIONS, raciTitle, StatusPill } from '../components/Badges';
import { Changes } from '../components/Changes';
import { MermaidDiagram, renderSvg } from '../components/MermaidDiagram';
import { StepEditor } from './StepEditor';

type Tab = 'steps' | 'raci' | 'diagram' | 'changes' | 'data';

const VIEW_LABEL: Record<DiagramView, string> = { swimlane: 'Swimlane by role', linear: 'Linear', valueStream: 'Value stream' };

/** Process detail: Steps, RACI, and Diagram are live; Data & documents arrives with the interview. */
export function ProcessDetail({ me }: { me: MeResponse | null }) {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const label = params.get('v') ?? 'current';
  const [detail, setDetail] = useState<ProcessDetailDto | null>(null);
  const [graph, setGraph] = useState<GraphResponseDto | null>(null);
  const [diff, setDiff] = useState<DiffResponseDto | null | 'none'>(null);
  const [revising, setRevising] = useState(false);
  const [approval, setApproval] = useState<ApprovalStateDto | null>(null);
  const [submitting, setSubmitting] = useState<'closed' | 'confirm' | 'busy'>('closed');
  const [bump, setBump] = useState<Exclude<VersionBump, 'none'>>('patch');
  const [deciding, setDeciding] = useState(false);
  const [flags, setFlags] = useState<FlagDto[]>([]);
  const [flagBusy, setFlagBusy] = useState(false);

  async function refreshFlags() {
    try {
      setFlags(await api.listFlags(slug));
    } catch {
      setFlags([]);
    }
  }

  async function flag(reason: string) {
    setFlagBusy(true);
    try {
      await api.addFlag(slug, reason);
      setSaved('Thank you — the owner will see your flag.');
      await refreshFlags();
    } catch (e) {
      setSaveError(describeError(e, 'The flag could not be sent.'));
    } finally {
      setFlagBusy(false);
    }
  }

  async function resolve(id: string) {
    setFlagBusy(true);
    try {
      await api.resolveFlag(slug, id);
      await refreshFlags();
    } catch (e) {
      setSaveError(describeError(e, 'The flag could not be resolved.'));
    } finally {
      setFlagBusy(false);
    }
  }

  const [publishing, setPublishing] = useState(false);

  /** D-003: the browser renders the swimlane; the API sanitises it and composes the PDF. */
  async function publish() {
    if (!graph) return;
    setPublishing(true);
    setSaveError(null);
    try {
      const svg = await renderSvg(graph.diagrams.swimlane);
      const r = await api.publish(slug, graph.version.label, svg);
      setSaved(`${r.republished ? 'Re-published' : 'Published'} ${r.fileName} (${Math.round(r.bytes / 1024)} KB) to SharePoint.`);
      setDetail(await api.getProcess(slug));
    } catch (e) {
      setSaveError(describeError(e, 'Publishing failed.'));
    } finally {
      setPublishing(false);
    }
  }

  async function recertify() {
    setFlagBusy(true);
    setSaveError(null);
    try {
      const r = await api.recertify(slug, null);
      setSaved(`Re-certified. Next review by ${r.nextRecertDate}.${sharePointNote(r.sharePoint)}`);
      setDetail(await api.getProcess(slug));
    } catch (e) {
      setSaveError(describeError(e, 'Could not re-certify.'));
    } finally {
      setFlagBusy(false);
    }
  }

  async function refreshApproval(l: string) {
    try {
      setApproval(await api.getApproval(slug, l));
    } catch {
      setApproval(null);
    }
  }

  async function submit() {
    setSubmitting('busy');
    setSaveError(null);
    try {
      const r = await api.submitVersion(slug, label, diff && diff !== 'none' ? bump : undefined);
      setSubmitting('closed');
      const routeText = r.route.map((s) => STAGE_LABEL[s.stage]).join(' → ');
      setSaved(
        r.approved
          ? `Submitted and approved as v${r.label}: Owner review was the only stage, and your submission is your confirmation.`
          : r.ownerReviewRecorded && r.pendingStage
            ? `Submitted as v${r.label}. Your submission counts as your owner review. Waiting on ${STAGE_LABEL[r.pendingStage].toLowerCase()}. Route: ${routeText}.`
            : `Submitted as v${r.label}. Route: ${routeText}.`,
      );
      setParams(r.label === (detail?.currentVersion ?? '') ? {} : { v: r.label });
    } catch (e) {
      setSubmitting('confirm');
      setSaveError(describeError(e, 'The version could not be submitted.'));
    }
  }

  async function decide(decision: 'Approved' | 'ChangesRequested' | 'Rejected', comment: string) {
    setDeciding(true);
    setSaveError(null);
    try {
      const r = await api.decide(slug, label, decision, comment.trim() || null);
      setSaved(r.approved ? 'Approved. This version is now the current version of the process.' : decision === 'Approved' ? `Approved; now waiting on ${r.nextStage}.` : `${decision === 'ChangesRequested' ? 'Changes requested' : 'Rejected'}; the owner has your comment.`);
      const [d, a] = await Promise.all([api.getProcess(slug), api.getApproval(slug, label)]);
      setDetail(d);
      setApproval(a);
      if (r.approved) setGraph((g) => (g ? { ...g, version: { ...g.version, approvedAt: a.approvedAt } } : g));
    } catch (e) {
      setSaveError(describeError(e, 'The decision could not be recorded.'));
    } finally {
      setDeciding(false);
    }
  }
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('steps');
  const [view, setView] = useState<DiagramView>('swimlane');
  const [showSource, setShowSource] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  // Removal and resumption (D-034).
  const navigate = useNavigate();
  const [interview, setInterview] = useState<ProcessInterviewDto | null>(null);
  const [removing, setRemoving] = useState(false);
  async function continueInterview() {
    setRemoving(true);
    setSaveError(null);
    try {
      const r = await api.resumeInterview(slug);
      navigate(`/interview/${r.sessionId}`);
    } catch (e) {
      setSaveError(describeError(e, 'The interview could not be resumed.'));
      setRemoving(false);
    }
  }
  async function deleteDraft() {
    if (!detail || !window.confirm(`Delete "${detail.name}" and everything captured for it? This cannot be undone.`)) return;
    setRemoving(true);
    try {
      await api.deleteProcess(slug);
      navigate('/');
    } catch (e) {
      setSaveError(describeError(e, 'The draft could not be deleted.'));
      setRemoving(false);
    }
  }
  async function retire() {
    if (!detail) return;
    const reason = window.prompt(`Retire "${detail.name}"? It leaves the library and its PDF in SharePoint is marked Retired; the record and history are kept.\n\nReason (optional):`);
    if (reason === null) return;
    setRemoving(true);
    try {
      const r = await api.retireProcess(slug, reason.trim() || null);
      setDetail((d) => (d ? { ...d, status: 'Retired' } : d));
      setSaved(
        `Retired.${r.sharePoint === 'updated' ? ' Its PDF in SharePoint is marked Retired.' : ''}${r.sharePoint === 'failed' ? ' SharePoint could not be updated: set Approval Status to Retired on its PDF there by hand.' : ''}`,
      );
    } catch (e) {
      setSaveError(describeError(e, 'The process could not be retired.'));
    } finally {
      setRemoving(false);
    }
  }
  async function restore() {
    if (!detail) return;
    const reason = window.prompt(`Restore "${detail.name}"? It returns to the library and its PDF goes back to SharePoint.\n\nReason (optional):`);
    if (reason === null) return;
    setRemoving(true);
    try {
      const r = await api.restoreProcess(slug, reason.trim() || null);
      setDetail((d) => (d ? { ...d, status: r.status as ProcessDetailDto['status'] } : d));
      setSaved(`Restored as ${r.status}.${r.republished.length ? ` Back in SharePoint: ${r.republished.join(', ')}.` : ''}${r.notRepublished.length ? ` Could not re-upload: ${r.notRepublished.join(', ')} — use Re-publish PDF.` : ''}`);
    } catch (e) {
      setSaveError(describeError(e, 'The process could not be restored.'));
    } finally {
      setRemoving(false);
    }
  }
  // Screenshots (D-033): keyed to this version's steps; refreshed after any change in the manager.
  const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
  const [lightbox, setLightbox] = useState<AttachmentDto | null>(null);
  const refreshAttachments = (l: string) =>
    api
      .listAttachments(slug, l)
      .then(setAttachments)
      .catch(() => setAttachments([]));

  async function startRevision() {
    setRevising(true);
    setSaveError(null);
    try {
      const r = await api.createRevision(slug);
      setParams({ v: r.versionLabel });
      setTab('steps');
      setSaved(`Revision ${r.versionLabel} opened from v${r.from}. The number is provisional until you submit.`);
    } catch (e) {
      setSaveError(describeError(e, 'The revision could not be started.'));
    } finally {
      setRevising(false);
    }
  }

  async function save(g: ProcessGraph) {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await api.saveGraph(slug, label, g);
      setGraph({ version: r.version, graph: r.graph, issues: r.issues, diagrams: r.diagrams });
      setDetail((d) => (d ? { ...d, name: r.graph.name, purpose: r.graph.purpose } : d));
      setEditing(false);
      const added = [...r.catalogAdditions.roles.map((x) => `role "${x}"`), ...r.catalogAdditions.systems.map((x) => `system "${x}"`)];
      setSaved(added.length ? `Saved. New in the catalog: ${added.join(', ')}.` : 'Saved.');
    } catch (e) {
      setSaveError(describeError(e, 'The draft could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    setDetail(null);
    setGraph(null);
    setDiff(null);
    setError(null);
    setEditing(false);
    Promise.all([api.getProcess(slug), api.getGraph(slug, label)])
      .then(([d, g]) => {
        setDetail(d);
        setGraph(g);
        const hasEarlier = d.versions.some((v) => v.approvedAt && v.id !== g.version.id);
        if (hasEarlier)
          api
            .getDiff(slug, g.version.label)
            .then((df) => {
              setDiff(df);
              if (df.diff.suggestedBump !== 'none') setBump(df.diff.suggestedBump);
            })
            .catch(() => setDiff('none'));
        else setDiff('none');
        void refreshApproval(g.version.label);
        void refreshFlags();
        void refreshAttachments(g.version.label);
        if (me && (me.isAdmin || me.oid === d.ownerOid)) api.getProcessInterview(slug).then(setInterview).catch(() => setInterview(null));
      })
      .catch((e: unknown) => setError(e instanceof ApiError && e.status === 404 ? 'No such process in your departments.' : describeError(e, 'The process could not be loaded.')));
  }, [slug, label]);

  if (error)
    return (
      <div className="notice notice--error">
        {error} <Link to="/">Back to the library</Link>
      </div>
    );
  if (!detail || !graph) return <div className="notice">Loading process.</div>;

  const errors = graph.issues.filter((i) => i.severity === 'error');
  const warnings = graph.issues.filter((i) => i.severity === 'warning');
  const isOwner = Boolean(me) && (me!.isAdmin || me!.oid === detail.ownerOid);
  const inApproval = Boolean(approval?.submittedAt) && !approval?.approvedAt;
  const canEdit = isOwner && !graph.version.approvedAt && !inApproval && detail.status !== 'Retired';
  const canSubmit = isOwner && !graph.version.approvedAt && !inApproval && detail.status !== 'Retired';
  const blocking = graph.issues.filter((i) => i.severity === 'error').length;
  const againstLabel = diff && diff !== 'none' ? diff.against : null;
  const previewLabel = againstLabel ? bumpLabel(againstLabel, bump) : '1.0.0';
  const canFlag = Boolean(me) && (detail.status === 'Published' || detail.status === 'Stale');
  const canRecertify = isOwner && (detail.status === 'Published' || detail.status === 'Stale');
  const banner =
    detail.status === 'Stale'
      ? 'This process is stale: its re-certification is overdue or readers have flagged it. The owner should review it and re-certify, or start a revision.'
      : detail.status === 'Orphaned'
        ? 'This process has no active owner (the owner’s account is disabled). The department head should assign a new owner.'
        : detail.nextRecertDate && detail.status === 'Published' && daysUntil(detail.nextRecertDate) <= 30
          ? `Re-certification due ${detail.nextRecertDate}${daysUntil(detail.nextRecertDate) < 0 ? ' (overdue)' : ''}.`
          : null;
  const openDraft = detail.versions.find((v) => !v.approvedAt);
  const hasApproved = detail.versions.some((v) => Boolean(v.approvedAt));
  const canRevise = isOwner && Boolean(graph.version.approvedAt) && !openDraft && detail.status !== 'Retired';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card__header">
          <div>
            <div className="card__title">{detail.name}</div>
            <div className="grid__secondary">{detail.purpose ?? 'No purpose recorded.'}</div>
          </div>
          <div className="card__meta" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <StatusPill status={detail.status} />
            <label className="version-pick">
              <span className="muted">Version</span>
              <select value={graph.version.label} onChange={(e) => setParams(e.target.value === (detail.currentVersion ?? '') ? {} : { v: e.target.value })} aria-label="Version">
                {detail.versions.map((v) => (
                  <option key={v.id} value={v.label}>
                    v{v.label} {v.approvedAt ? '· approved' : '· draft'}
                  </option>
                ))}
              </select>
            </label>
            {graph.version.approvedAt && (() => {
              const v = detail.versions.find((x) => x.id === graph.version.id);
              return (
                <>
                  {v?.sharepointUrl && (
                    <a href={v.sharepointUrl} target="_blank" rel="noreferrer" className="link-button">
                      Open in SharePoint
                    </a>
                  )}
                  {/* One file per process (D-065): only the current version is published. */}
                  {isOwner && graph.version.label === detail.currentVersion && (
                    <button type="button" className="button button--secondary" onClick={publish} disabled={publishing}>
                      {publishing ? 'Publishing…' : v?.sharepointUrl ? 'Re-publish PDF' : 'Publish to SharePoint'}
                    </button>
                  )}
                </>
              );
            })()}
            {canRevise && (
              <button type="button" className="button button--secondary" onClick={startRevision} disabled={revising}>
                {revising ? 'Opening…' : 'Start a revision'}
              </button>
            )}
            {openDraft && graph.version.approvedAt && (
              <button type="button" className="link-button" onClick={() => setParams({ v: openDraft.label })}>
                Open draft v{openDraft.label}
              </button>
            )}
            {canSubmit && submitting === 'closed' && (
              <button type="button" className="button" onClick={() => setSubmitting('confirm')} disabled={blocking > 0 || editing} title={blocking > 0 ? `Fix ${blocking} blocking issue${blocking === 1 ? '' : 's'} first` : undefined}>
                Submit for approval
              </button>
            )}
            {isOwner && openDraft && !inApproval && detail.status !== 'Retired' && (
              <button type="button" className="button button--secondary" onClick={continueInterview} disabled={removing || editing} title={interview && !interview.completedAt ? 'Pick up the unfinished interview' : 'Open a new interview session that continues from the record so far'}>
                {interview && !interview.completedAt ? 'Continue the interview' : 'Resume the interview'}
              </button>
            )}
            {isOwner && !hasApproved && (
              <button type="button" className="link-button link-button--danger" onClick={deleteDraft} disabled={removing || editing}>
                Delete draft
              </button>
            )}
            {isOwner && hasApproved && detail.status !== 'Retired' && (
              <button type="button" className="link-button link-button--danger" onClick={retire} disabled={removing || editing}>
                Retire
              </button>
            )}
            {isOwner && detail.status === 'Retired' && (
              <button type="button" className="button button--secondary" onClick={restore} disabled={removing}>
                Restore
              </button>
            )}
          </div>
        </div>
        {submitting !== 'closed' && (
          <div className="submit-confirm">
            <div>
              <strong>Submit v{graph.version.label} for approval?</strong>{' '}
              {againstLabel ? (
                <>
                  It will become <span className="mono">v{previewLabel}</span> — change type{' '}
                  <select value={bump} onChange={(e) => setBump(e.target.value as Exclude<VersionBump, 'none'>)} aria-label="Change type">
                    <option value="patch">wording or timing (patch)</option>
                    <option value="minor">steps or flow (minor)</option>
                    <option value="major">role or system (major)</option>
                  </select>
                  {diff && diff !== 'none' && diff.diff.suggestedBump !== 'none' && <span className="muted"> · suggested from the changes: {diff.diff.suggestedBump}</span>}
                </>
              ) : (
                <>
                  It will become <span className="mono">v1.0.0</span>, the first approved version.
                </>
              )}
            </div>
            <div className="approval__buttons">
              <button type="button" className="button" onClick={submit} disabled={submitting === 'busy'}>
                {submitting === 'busy' ? 'Submitting…' : 'Submit'}
              </button>
              <button type="button" className="button button--secondary" onClick={() => setSubmitting('closed')} disabled={submitting === 'busy'}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {approval && (approval.submittedAt || approval.approvedAt) && (
          <div id="approval">
            <ApprovalPanel state={approval} onDecide={decide} busy={deciding} />
          </div>
        )}
        <dl className="kv">
          <dt>Department</dt>
          <dd>{detail.departmentName}</dd>
          <dt>Owner</dt>
          <dd>{detail.ownerName ?? <span className="muted">Not yet signed in</span>}</dd>
          <dt>Maintained by</dt>
          <dd>{detail.maintainerRole}</dd>
          <dt>Versions</dt>
          <dd className="mono">{detail.versions.map((v) => v.label).join(', ')}</dd>
          {graph.version.automationOverride && (
            <>
              <dt>Compliance gate</dt>
              <dd>Required — a flagged step is performed outside its default.</dd>
            </>
          )}
        </dl>
      </div>

      <WalkthroughVideos slug={slug} canEdit={isOwner && Boolean(openDraft) && !inApproval && detail.status !== 'Retired'} />

      <div className="card">
        <div className="tabs" role="tablist">
          {(['steps', 'raci', 'diagram', 'changes', 'data'] as Tab[]).map((t) => (
            <button
              key={t}
              role="tab"
              type="button"
              aria-selected={tab === t}
              className={`tab${tab === t ? ' tab--active' : ''}`}
              disabled={t === 'data' || (t === 'changes' && diff === 'none')}
              title={t === 'data' ? 'Arrives in a later phase' : t === 'changes' && diff === 'none' ? 'No earlier approved version to compare with' : undefined}
              onClick={() => setTab(t)}
            >
              {{ steps: 'Steps', raci: 'RACI', diagram: 'Diagram', changes: 'Changes', data: 'Data & documents' }[t]}
            </button>
          ))}
        </div>
        {banner && (
          <div className={`issue${detail.status === 'Orphaned' || detail.status === 'Stale' ? ' issue--error' : ''}`} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span>{banner}</span>
            {detail.status === 'Orphaned' && me?.isAdmin && (
              <Link to="/admin" className="button button--secondary" style={{ marginLeft: 'auto' }}>
                Assign a new owner
              </Link>
            )}
            {canRecertify && (
              <button type="button" className="button button--secondary" style={{ marginLeft: 'auto' }} disabled={flagBusy} onClick={recertify}>
                Still accurate — re-certify
              </button>
            )}
          </div>
        )}
        {saved && !editing && <div className="issue issue--ok">{saved}</div>}
        {!editing &&
          errors.map((i, n) => (
            <div key={`e${n}`} className="issue issue--error">
              {i.message}
            </div>
          ))}
        {!editing &&
          warnings.map((i, n) => (
            <div key={`w${n}`} className="issue">
              {i.message}
            </div>
          ))}
        {tab === 'steps' && canEdit && !editing && (
          <div className="toolbar">
            <span className="muted">Draft — you can change steps here without another interview.</span>
            <button type="button" className="button button--secondary" style={{ marginLeft: 'auto' }} onClick={() => { setEditing(true); setSaved(null); }}>
              Edit steps
            </button>
          </div>
        )}
        {tab === 'steps' && editing && (
          <>
            <StepEditor key={graph.version.id} initial={graph.graph} onSave={save} onCancel={() => setEditing(false)} saving={saving} saveError={saveError} />
            <ScreenshotManager slug={slug} steps={graph.graph.steps} items={attachments} onChanged={() => void refreshAttachments(graph.version.label)} />
          </>
        )}
        {tab === 'steps' && !editing && <StepsTable steps={graph.graph.steps} slug={slug} attachments={attachments} onOpen={setLightbox} />}
        <Lightbox slug={slug} attachment={lightbox} onClose={() => setLightbox(null)} />
        {tab === 'changes' && diff && diff !== 'none' && <Changes diff={diff.diff} against={diff.against} />}
        {tab === 'changes' && diff === null && <div className="notice" style={{ margin: 18 }}>Comparing versions.</div>}
        {(canFlag || flags.length > 0) && <FlagsPanel flags={flags} canFlag={canFlag} canResolve={isOwner} onFlag={flag} onResolve={resolve} busy={flagBusy} />}
        {tab === 'raci' && <RaciMatrix steps={graph.graph.steps} />}
        {tab === 'diagram' && (
          <div>
            <div className="toolbar">
              <div className="segmented" role="radiogroup" aria-label="Diagram view">
                {(Object.keys(VIEW_LABEL) as DiagramView[]).map((v) => (
                  <button key={v} type="button" role="radio" aria-checked={view === v} className={`segmented__item${view === v ? ' segmented__item--active' : ''}`} onClick={() => setView(v)}>
                    {VIEW_LABEL[v]}
                  </button>
                ))}
              </div>
              <div className="legend" aria-hidden="true">
                <span className="performer performer--human">Human</span>
                <span className="performer performer--agent">Agent</span>
                <span className="performer performer--hybrid">Hybrid</span>
                <span className="muted">Thick line = usual path</span>
              </div>
              <button type="button" className="link-button" onClick={() => setShowSource((s) => !s)}>
                {showSource ? 'Hide Mermaid source' : 'Show Mermaid source'}
              </button>
            </div>
            <MermaidDiagram source={graph.diagrams[view]} />
            {showSource && <pre className="source">{graph.diagrams[view]}</pre>}
            <p className="muted diagram__note">Drawn from the steps and transitions. To change the diagram, change the steps.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function daysUntil(isoDate: string): number {
  const today = new Date();
  const t = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const [y, m, d] = isoDate.split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - t) / 86_400_000);
}

function StepsTable({ steps, slug, attachments, onOpen }: { steps: Step[]; slug: string; attachments: AttachmentDto[]; onOpen: (a: AttachmentDto) => void }) {
  return (
    <table className="grid">
      <thead>
        <tr>
          <th>#</th>
          <th>Step</th>
          <th>Role</th>
          <th>Performed by</th>
          <th>Systems</th>
          <th>Touch</th>
          <th>Wait</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s, i) => (
          <tr key={s.key}>
            <td className="mono muted">{i + 1}</td>
            <td>
              <div className="grid__primary">
                {s.name}
                {s.stepType === 'decision' && <span className="tag tag--decision">DECISION</span>}
                {s.stepType === 'wait' && <span className="tag">WAIT</span>}
              </div>
              {s.description && <div className="grid__secondary">{s.description}</div>}
              {s.whyNotAutomate && <div className="grid__secondary">Stays human: {s.whyNotAutomate}</div>}
              <StepScreenshots slug={slug} items={attachments.filter((a) => a.stepKey === s.key)} onOpen={onOpen} />
            </td>
            <td>{s.role}</td>
            <td>
              <Performer value={s.performedBy} />
            </td>
            <td>{s.systems.map((x) => x.name).join(', ') || '—'}</td>
            <td className="mono">{minutes(s.touchMinutes)}</td>
            <td className="mono">{minutes(s.waitMinutes)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const LETTERS: RaciLetter[] = ['R', 'A', 'C', 'I'];

function RaciMatrix({ steps }: { steps: Step[] }) {
  const roles = useMemo(() => {
    const set = new Set<string>();
    for (const s of steps) {
      set.add(s.role);
      for (const r of s.raci) set.add(r.role);
    }
    return [...set].sort();
  }, [steps]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div className="muted" style={{ padding: '10px 18px 0', fontSize: 12 }} aria-label="RACI legend">
        {LETTERS.map((l) => (
          <span key={l} title={`${RACI_DEFINITIONS[l].title}: ${RACI_DEFINITIONS[l].meaning}`} style={{ marginRight: 14, cursor: 'help' }}>
            <strong className="mono">{l}</strong> {RACI_DEFINITIONS[l].title}
          </span>
        ))}
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th>Step</th>
            {roles.map((r) => (
              <th key={r}>{r}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {steps
            .filter((s) => s.stepType !== 'start' && s.stepType !== 'end')
            .map((s) => (
              <tr key={s.key}>
                <td className="grid__primary">{s.name}</td>
                {roles.map((r) => {
                  const letters = LETTERS.filter((l) => s.raci.some((x) => x.role === r && x.letter === l));
                  return (
                    <td key={r} className="mono" style={{ textAlign: 'center', fontWeight: letters.includes('A') ? 700 : 500, cursor: letters.length ? 'help' : undefined }} title={letters.length ? `${r} — ${s.name}\n${raciTitle(letters)}` : undefined}>
                      {letters.join('') || <span className="muted">·</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
